import React, { useEffect, useRef, useCallback, useState } from "react";
import "./AllCustomers.css";
import { InputText } from "primereact/inputtext";
import {
  Calendar,
  DownloadIcon,
  NextIcon,
  PrevIcon,
  Search,
  SelectorIcon,
  Status,
} from "../../../../../../utility";
import { OverlayPanel } from "primereact/overlaypanel";
import { useFormik } from "formik";
import moment from "moment";
import Calendardropdown from "./Calendar/Calendardropdown";
import { debounce } from "lodash";
import { api } from "../../../../../../utility/AxiosInceptor";
import { Toast } from "primereact";
import CommonLoaderBlue from "../../../../../components/Loader/LoaderBlue";
import { Button } from "primereact/button";

// Constants
const listLimit = 18;

const AllCustomers = ({ setRefreshCustomerData, setShowAllCustomers }) => {
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [floatPagesLeft, setFloatPagesLeft] = useState([]);
  const [floatPagesRight, setFloatPagesRight] = useState([]);
  const [secondTimeCalled, setSecondTimeCalled] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [allUserList, setAllUserList] = useState([]);
  const [isFirstTimeCalledApi, setIsFirstTimeCalledApi] = useState(false);
  const [currentPagination, setCurrentPagination] = useState({});
  const [value, setValue] = useState("");
  const [selectedRange, setSelectedRange] = useState([null, null]);
  const [shortSelection, setShortSelection] = useState();

  // Refs
  const toast = useRef();
  const op = useRef(null);
  const stat = useRef(null);
  const calendar = useRef(null);

  // User data from localStorage
  const loginRes = JSON.parse(localStorage.getItem("userData"));

  // Formik form for filter management
  const formik = useFormik({
    initialValues: {
      prepaid: false,
      postpaid: false,
      acp: false,
      active: false,
      prospect: false,
      suspended: false,
      disconnected: false,
      cancelled: false,
      all: false,
    },
    onSubmit: () => {},
  });

  // Handler for "All" checkbox change
  const handleAllChange = (checked) => {
    formik.setValues(() => ({
      all: checked,
      active: checked,
      prospect: checked,
      suspended: checked,
      disconnected: checked,
      cancelled: checked,
    }));
  };

  // Handler for individual checkbox changes
  const handleIndividualChange = (field, checked) => {
    formik.setFieldValue(field, checked);
    if (!checked) {
      formik.setFieldValue("all", false);
    } else {
      const allChecked = [
        "active",
        "prospect",
        "suspended",
        "disconnected",
        "cancelled",
      ].every((key) => (key === field ? checked : formik.values[key]));
      if (allChecked) {
        formik.setFieldValue("all", true);
      }
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [formik.values, selectedRange, value]);

  // Debounced API call to fetch users
  const fetchUsers = useCallback(
    debounce(() => {
      // Prepare account type filters
      let accountTypeSelected = [];
      if (formik.values.prepaid) accountTypeSelected.push("Prepaid");
      if (formik.values.postpaid) accountTypeSelected.push("Postpaid");
      if (formik.values.acp) accountTypeSelected.push("Acp");

      // Prepare status filters
      let statusSelected = [];
      if (formik.values.active) statusSelected.push("active");
      if (formik.values.disconnected) statusSelected.push("disconnected");
      if (formik.values.prospect) statusSelected.push("prospect");
      if (formik.values.suspended) statusSelected.push("suspended");
      if (formik.values.cancelled) statusSelected.push("cancelled");

      // API call to fetch users
      api
        .get(
          `/api/user/all?serviceProvider=${loginRes?.company}&endDate=${
            selectedRange[1]
              ? moment(new Date(selectedRange[1])).format("MM-DD-YYYY")
              : ""
          }&startDate=${
            selectedRange[0]
              ? moment(new Date(selectedRange[0])).format("MM-DD-YYYY")
              : ""
          }&page=${currentPage}&search=${value}&limit=${listLimit}&accountType=${accountTypeSelected.join(
            ",",
          )}&status=${statusSelected.join(",")}`,
        )
        .then((res) => {
          setAllUserList(res?.data?.data);
          setCurrentPagination(res?.data?.pagination);

          // Setup pagination for more than 6 pages
          if (res?.data?.pagination.totalPages > 6) {
            if (
              floatPagesLeft.length === 0 ||
              res?.data?.pagination?.totalPages !== currentPagination.totalPages
            ) {
              setFloatPagesLeft([1, 2, 3]);
              setFloatPagesRight([
                res?.data?.pagination.totalPages - 1,
                res?.data?.pagination.totalPages,
              ]);
            }
          }

          // Track API call status
          if (isFirstTimeCalledApi) {
            setSecondTimeCalled(true);
          }
          setIsFirstTimeCalledApi(true);
        })
        .catch((error) => {
          // Handle API error
          if (isFirstTimeCalledApi) {
            setSecondTimeCalled(true);
          }
          setIsFirstTimeCalledApi(true);
          toast.current.show({
            severity: "error",
            summary: "info",
            detail:
              error?.response?.data?.message || "Error Fetch Customer List",
          });
        });
    }, 500), // 500ms debounce time
    [formik.values, selectedRange, currentPage, value],
  );

  // Trigger fetchUsers when dependencies change
  useEffect(() => {
    fetchUsers();
    return () => fetchUsers.cancel(); // Cleanup function to avoid unnecessary calls
  }, [fetchUsers]);

  return (
    <div className="allcustomersmain">
      <Toast ref={toast} />

      {/* Navigation header with filters and search */}
      <div className="flex w-full justify-content-between align-items-center nav">
        <div className="flex align-items-center">
          <h1 style={{ marginTop: "1rem" }}>Customer Profile</h1>
        </div>

        {/* Filter controls - only show when data is available */}
        {((isFirstTimeCalledApi && allUserList?.length > 0) ||
          secondTimeCalled) && (
          <div
            className="flex lign-items-center"
            style={{ gap: "2rem", height: "4rem" }}
          >
            {/* Search input */}
            <div className="inputouter">
              <span>
                <Search />
              </span>
              <InputText
                className="input"
                type="search"
                placeholder="Search Here"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>

            {/* Calendar filter */}
            <div>
              <button
                className="calendar"
                onClick={(e) => calendar.current.toggle(e)}
              >
                <Calendar />
              </button>
              <OverlayPanel ref={calendar} className="calendaroverlay">
                <Calendardropdown
                  calendar={calendar}
                  setSelectedRange={setSelectedRange}
                  setShortSelection={setShortSelection}
                  shortSelection={shortSelection}
                  selectedRange={selectedRange}
                />
              </OverlayPanel>
            </div>

            {/* Account type filter */}
            <div>
              <button
                className="selector"
                onClick={(e) => op.current.toggle(e)}
              >
                <SelectorIcon />
              </button>
              <OverlayPanel ref={op}>
                <div className="flex flex-column moduleselectorallcustomer  overlay">
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="prepaid"
                      name="prepaid"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        formik.setFieldValue("prepaid", e.target.checked)
                      }
                      checked={formik.values.prepaid}
                    />
                    <label htmlFor="prepaid">Prepaid</label>
                  </div>
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="postpaid"
                      name="postpaid"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        formik.setFieldValue("postpaid", e.target.checked)
                      }
                      checked={formik.values.postpaid}
                    />
                    <label htmlFor="postpaid">Postpaid</label>
                  </div>
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="acp"
                      name="acp"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        formik.setFieldValue("acp", e.target.checked)
                      }
                      checked={formik.values.acp}
                    />
                    <label htmlFor="acp">ACP</label>
                  </div>
                </div>
              </OverlayPanel>
            </div>

            {/* Status filter */}
            <div>
              <button
                className="statusselect"
                onClick={(e) => stat.current.toggle(e)}
              >
                <Status />
              </button>
              <OverlayPanel ref={stat} className="overlaystatus">
                <div className="flex flex-column enrollmentallcustomerselector overlay w-full ">
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="active"
                      name="active"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        handleIndividualChange("active", e.target.checked)
                      }
                      checked={formik.values.active}
                    />
                    <label htmlFor="active">Active</label>
                  </div>
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="prospect"
                      name="prospect"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        handleIndividualChange("prospect", e.target.checked)
                      }
                      checked={formik.values.prospect}
                    />
                    <label htmlFor="prospect">Prospect</label>
                  </div>
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="suspended"
                      name="suspended"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        handleIndividualChange("suspended", e.target.checked)
                      }
                      checked={formik.values.suspended}
                    />
                    <label htmlFor="suspended">Suspended</label>
                  </div>
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="disconnected"
                      name="disconnected"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        handleIndividualChange("disconnected", e.target.checked)
                      }
                      checked={formik.values.disconnected}
                    />
                    <label htmlFor="disconnected">Disconnected</label>
                  </div>
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="cancelled"
                      name="cancelled"
                      className="check"
                      type="checkbox"
                      onChange={(e) =>
                        handleIndividualChange("cancelled", e.target.checked)
                      }
                      checked={formik.values.cancelled}
                    />
                    <label htmlFor="cancelled">Cancelled</label>
                  </div>
                  <div
                    className="flex justify-content-start align-items-center"
                    style={{ gap: "0.4rem", height: "3.6rem" }}
                  >
                    <input
                      id="all"
                      name="all"
                      className="check"
                      type="checkbox"
                      onChange={(e) => handleAllChange(e.target.checked)}
                      checked={formik.values.all}
                    />
                    <label htmlFor="all">ALL</label>
                  </div>
                </div>
              </OverlayPanel>
            </div>

            {/* Export button */}
            <button
              onClick={async () => {
                setIsExportLoading(true);
                let accountTypeSelected = [];
                if (formik.values.prepaid) accountTypeSelected.push("Prepaid");
                if (formik.values.postpaid)
                  accountTypeSelected.push("Postpaid");
                if (formik.values.acp) accountTypeSelected.push("Acp");

                let statusSelected = [];
                if (formik.values.active) statusSelected.push("active");
                if (formik.values.disconnected)
                  statusSelected.push("disconnected");
                if (formik.values.prospect) statusSelected.push("prospect");
                if (formik.values.suspended) statusSelected.push("suspended");
                if (formik.values.cancelled) statusSelected.push("cancelled");

                // API call to export customer data
                api
                  .get(
                    `/api/user/exportCustomerData?serviceProvider=${loginRes?.company}&endDate=${
                      selectedRange[1]
                        ? moment(new Date(selectedRange[1])).format(
                            "MM-DD-YYYY",
                          )
                        : ""
                    }&startDate=${
                      selectedRange[0]
                        ? moment(new Date(selectedRange[0])).format(
                            "MM-DD-YYYY",
                          )
                        : ""
                    }&page=${currentPage}&search=${value}&limit=${listLimit}&accountType=${accountTypeSelected.join(
                      ",",
                    )}&status=${statusSelected.join(",")}`,
                    { responseType: "blob" }, // Ensure binary response
                  )
                  .then((response) => {
                    // Extract filename from response headers
                    const contentDisposition =
                      response.headers["content-disposition"];
                    let filename = "CustomerData.xlsx";
                    if (contentDisposition) {
                      const match = contentDisposition.match(
                        /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/,
                      );
                      if (match) {
                        filename = decodeURIComponent(match[1] || match[2]);
                      }
                    }

                    // Create and download the blob
                    const blob = new Blob([response.data], {
                      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);

                    setIsExportLoading(false);
                  })
                  .catch((error) => {
                    setIsExportLoading(false);
                    toast.current.show({
                      severity: "error",
                      summary: "Export Customer",
                      detail:
                        error?.response?.data?.message || "Exporting Customers",
                    });
                  });
              }}
              className="export backbtn flex justify-content-center align-items-center"
            >
              {" "}
              {!isExportLoading ? (
                <div
                  className="flex flex-wrap flex-row justify-content-start"
                  style={{
                    marginTop: "-0.6rem",
                    height: "1.6rem",
                    width: "6.9rem",
                    gap: "0.8rem",
                  }}
                >
                  <p
                    style={{
                      padding: "0rem",
                      fontWeight: 500,
                      fontSize: "1.4rem",
                      margin: "0rem",
                    }}
                  >
                    Export
                  </p>
                  <div style={{ marginTop: "0.3rem", marginLeft: "0.8rem" }}>
                    <DownloadIcon />
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap flex-row justify-content-center align-items-center">
                  <CommonLoaderBlue color={"royalblue"} sizehere={14} />
                </div>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Customer list */}
      <div
        className="flex flex-wrap justify-content-start"
        style={{ gap: "0.8rem", width: "108.4rem", marginTop: "2.4rem" }}
      >
        {/* Loading state */}
        {!isFirstTimeCalledApi ? (
          <div className="flex mt-4  flex-wrap flex-row justify-content-center align-items-center w-full">
            <CommonLoaderBlue sizehere={18} />
          </div>
        ) : (
          // Customer cards
          allUserList.map((customer) => (
            <div
              onClick={() => {
                localStorage.setItem(
                  "selectedId",
                  JSON.stringify(customer._id),
                );
                setShowAllCustomers(false);
                setRefreshCustomerData((prev) => !prev);
              }}
              className="cust cursor-pointer flex justify-content-evenly align-items-center"
            >
              <div
                className="flex flex-column justify-content-between align-items-start"
                style={{ width: "13.5rem", height: "7.2rem" }}
              >
                <button className="custbtn cursor-pointer">
                  {customer?.firstName?.substring(0, 1)?.toUpperCase()}
                  {customer?.lastName?.substring(0, 1)?.toUpperCase()}
                </button>
                <p>
                  {customer?.firstName} {customer?.lastName}
                </p>
              </div>
              <div
                className="flex flex-column justify-content-evenly align-items-start pl-3"
                style={{
                  width: "18.1rem",
                  height: "6.6rem",
                  borderLeft: "1px solid #EFF2F8",
                }}
              >
                <div
                  className="flex flex-column justify-content-between"
                  style={{ height: "6.6rem" }}
                >
                  <h1
                    style={{
                      color:
                        customer?.status === "prospect"
                          ? "#4A90E2"
                          : customer.status === "disconnected"
                            ? "#E74C3C"
                            : customer.status === "active"
                              ? "#27AE60"
                              : customer.status === "inActive"
                                ? "#E74C3C"
                                : customer.status === "rejected"
                                  ? "#72728F"
                                  : customer.status === "cancelled"
                                    ? "#72728F"
                                    : customer.status === "suspended"
                                      ? "#F39C12"
                                      : "",
                    }}
                  >
                    {" "}
                    {customer?.status === "prospect"
                      ? "Prospect"
                      : customer.status === "disconnected"
                        ? "Disconnected"
                        : customer.status === "active"
                          ? "Active"
                          : customer.status === "inActive"
                            ? "InActive"
                            : customer.status === "rejected"
                              ? "Rejected"
                              : customer.status === "cancelled"
                                ? "Cancelled"
                                : customer.status === "suspended"
                                  ? "Suspended"
                                  : ""}
                  </h1>
                  <label>{customer?.enrollmentId}</label>
                  <h6>{customer?.contact}</h6>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div
        style={{ marginTop: "2.4rem" }}
        className="flex flex-wrap w-full  flex-row justify-content-between align-items-center "
      >
        {/* Results counter */}
        {isFirstTimeCalledApi && (
          <h1 className="infoenrollmentsall">
            {currentPagination?.total
              ? currentPagination?.page * currentPagination.limit >
                currentPagination.total
                ? currentPagination.total
                : currentPagination?.page * currentPagination.limit
              : "0"}
            /{currentPagination?.total ? currentPagination?.total : "0"}
          </h1>
        )}

        {/* Pagination controls */}
        {currentPagination?.totalPages > 1 && isFirstTimeCalledApi && (
          <div
            style={{}}
            className="flex flex flex-wrap flex-row justify-content-between pages"
          >
            {/* Previous button */}
            <button
              disabled={currentPage === 1}
              onClick={() => {
                let pagetocheck = currentPage;
                pagetocheck--;
                if (currentPagination.totalPages <= 6 && pagetocheck > 0) {
                  setCurrentPage(pagetocheck);
                } else if (
                  floatPagesLeft.includes(pagetocheck) ||
                  floatPagesRight.includes(pagetocheck)
                ) {
                  setCurrentPage(pagetocheck);
                } else if (pagetocheck > 0) {
                  if (pagetocheck === floatPagesLeft[0] - 1) {
                    let arr = floatPagesLeft;

                    arr.pop();
                    arr.unshift(floatPagesLeft[0] - 1);
                    setFloatPagesLeft(arr);
                    setCurrentPage(pagetocheck);
                  } else if (pagetocheck === floatPagesRight[0] - 1) {
                    let arr = floatPagesRight;

                    arr.pop();
                    arr.unshift(floatPagesRight[0] - 1);
                    setFloatPagesRight(arr);
                    setCurrentPage(pagetocheck);
                  }
                }
              }}
              className="cursor-pointer backbtn"
              style={{
                fontSize: "1.4rem",
                fontWeight: "500",
                opacity: currentPage === 1 && "0.6",
                backgroundColor: "#FFFFFF33",
                width: "10.6rem",
                height: "3.8rem",
                margin: "0rem",
              }}
            >
              <span>
                <PrevIcon /> &nbsp; Previous
              </span>
            </button>

            {/* Page numbers */}
            <div
              style={{ maxWidth: "26.9rem", gap: "1rem" }}
              className="flex flex-wrap flex-row justify-content-start align-items-start"
            >
              {/* Simple pagination for 6 or fewer pages */}
              {currentPagination.totalPages <= 6 ? (
                [...Array(currentPagination.totalPages)].map((_, index) => (
                  <Button
                    onClick={() => {
                      setCurrentPage(index + 1);
                    }}
                    style={{ width: "3.1rem", height: "3.4rem" }}
                    key={index + 1}
                    className={
                      currentPagination.page === index + 1
                        ? "pagecounteractive"
                        : "pagecounter"
                    }
                    label={index + 1}
                  />
                ))
              ) : (
                /* Complex pagination for more than 6 pages */
                <>
                  {floatPagesLeft.map((page) => (
                    <Button
                      key={page}
                      onClick={() => {
                        setCurrentPage(page);
                      }}
                      style={{ width: "3.1rem", height: "3.4rem" }}
                      className={
                        currentPagination.page === page
                          ? "pagecounteractive"
                          : "pagecounter"
                      }
                      label={page}
                    />
                  ))}
                  {floatPagesLeft[floatPagesLeft.length - 1] + 1 !==
                    floatPagesRight[0] && (
                    <Button className="pagecounter" label={"..."} />
                  )}

                  {floatPagesRight.map((page) => (
                    <Button
                      onClick={() => {
                        setCurrentPage(page);
                      }}
                      key={page}
                      className={
                        currentPagination.page === page
                          ? "pagecounteractive"
                          : "pagecounter"
                      }
                      label={page}
                    />
                  ))}
                </>
              )}
            </div>

            {/* Next button */}
            <button
              disabled={currentPage === currentPagination.totalPages}
              onClick={() => {
                let pagetocheck = currentPage;
                pagetocheck++;
                if (
                  currentPagination.totalPages <= 6 &&
                  pagetocheck > 0 &&
                  pagetocheck <= currentPagination.totalPages
                ) {
                  setCurrentPage(pagetocheck);
                } else if (
                  floatPagesLeft.includes(pagetocheck) ||
                  floatPagesRight.includes(pagetocheck)
                ) {
                  setCurrentPage(pagetocheck);
                } else if (pagetocheck <= currentPagination.totalPages) {
                  if (
                    pagetocheck ===
                    floatPagesLeft[floatPagesLeft.length - 1] + 1
                  ) {
                    let arr = floatPagesLeft;

                    arr.shift();
                    arr.push(pagetocheck);
                    setFloatPagesLeft(arr);
                    setCurrentPage(pagetocheck);
                  } else {
                    if (
                      pagetocheck ===
                        floatPagesRight[floatPagesRight.length - 1] + 1 &&
                      pagetocheck <= currentPagination.totalPages
                    ) {
                      let arr = floatPagesRight;

                      arr.shift();
                      arr.push(pagetocheck);
                      setFloatPagesRight(arr);
                      setCurrentPage(pagetocheck);
                    }
                  }
                }
              }}
              className="cursor-pointer backbtn"
              style={{
                fontSize: "1.4rem",
                fontWeight: "500",
                opacity: currentPage === currentPagination.totalPages && "0.6",
                backgroundColor: "#FFFFFF33",
                width: "8rem",
                height: "3.8rem",
              }}
            >
              <span className="flex align-items-center">
                Next &nbsp;
                <NextIcon />
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllCustomers;
